import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { PhoneCall, Users, TrendingUp, DollarSign, Radio, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { CallOutcome } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { dashboardApi, callsApi } from '@/lib/api/endpoints';
import { CALL_STATUS_LABEL, CALL_STATUS_VARIANT, OUTCOME_LABEL, fmtDuration, fmtPct, fmtPhone, fmtRelative, fmtUsd } from '@/lib/format';
import { StatusBadge } from '@/components/StatusBadge';

// Paleta validada (dataviz): laranja + ciano passam CVD/contraste em claro e escuro
const COLOR_CALLS = '#ea580c';
const COLOR_RECOVERED = '#0891b2';

export function DashboardPage() {
  const stats = useQuery({ queryKey: ['dashboard', 14], queryFn: () => dashboardApi.get(14), refetchInterval: 30_000 });
  const recent = useQuery({ queryKey: ['calls', 'recent'], queryFn: () => callsApi.list({ pageSize: 8 }), refetchInterval: 15_000 });

  const s = stats.data;
  return (
    <div>
      <PageHeader title="Painel" description="Visão geral das ligações de recuperação de carrinho" />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Stat icon={Users} label="Leads" value={s?.leads.total} hint={s ? `${s.leads.new} novos` : undefined} loading={stats.isLoading} />
        <Stat icon={PhoneCall} label="Chamadas hoje" value={s?.calls.today} hint={s ? `${s.calls.total} no total` : undefined} loading={stats.isLoading} />
        <Stat icon={Radio} label="Em andamento" value={s?.calls.inProgress} hint="agora" loading={stats.isLoading} pulse={!!s?.calls.inProgress} />
        <Stat
          icon={TrendingUp}
          label="Taxa de recuperação"
          value={s ? fmtPct(s.calls.recoveryRate) : undefined}
          hint={s ? `${s.leads.recovered} vendas recuperadas · contato ${fmtPct(s.calls.contactRate)}` : undefined}
          loading={stats.isLoading}
        />
        <Stat icon={DollarSign} label="Custo total" value={s ? fmtUsd(s.calls.totalCostUsd) : undefined} hint={s ? `média ${fmtDuration(s.calls.avgDurationSec)}/chamada` : undefined} loading={stats.isLoading} />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Chamadas por dia</CardTitle>
            <CardDescription>Últimos 14 dias</CardDescription>
          </CardHeader>
          <CardContent className="h-72">
            {stats.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : s && s.daily.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={s.daily} margin={{ left: -16, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="gCalls" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLOR_CALLS} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={COLOR_CALLS} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gRec" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLOR_RECOVERED} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={COLOR_RECOVERED} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="var(--border)" />
                  <XAxis dataKey="date" tickFormatter={(d: string) => format(new Date(d + 'T12:00:00'), 'dd/MM', { locale: ptBR })} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--foreground)' }}
                    labelFormatter={(d) => format(new Date(String(d) + 'T12:00:00'), "dd 'de' MMMM", { locale: ptBR })}
                    formatter={(v, name) => [v, name === 'calls' ? 'Chamadas' : 'Recuperadas']}
                  />
                  <Area type="monotone" dataKey="calls" name="calls" stroke={COLOR_CALLS} strokeWidth={2} fill="url(#gCalls)" dot={false} activeDot={{ r: 4 }} />
                  <Area type="monotone" dataKey="recovered" name="recovered" stroke={COLOR_RECOVERED} strokeWidth={2} fill="url(#gRec)" dot={false} activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Nenhuma chamada nos últimos 14 dias.</div>
            )}
            <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-sm" style={{ background: COLOR_CALLS }} />Chamadas</span>
              <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-sm" style={{ background: COLOR_RECOVERED }} />Recuperadas</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resultados</CardTitle>
            <CardDescription>Distribuição por desfecho</CardDescription>
          </CardHeader>
          <CardContent>
            {stats.isLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : s && s.outcomes.length ? (
              <ul className="space-y-3">
                {[...s.outcomes]
                  .sort((a, b) => b.count - a.count)
                  .map((o) => {
                    const total = s.outcomes.reduce((acc, x) => acc + x.count, 0);
                    const pct = total ? o.count / total : 0;
                    return (
                      <li key={o.outcome} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{OUTCOME_LABEL[o.outcome as CallOutcome] ?? o.outcome}</span>
                          <span className="tabular-nums text-muted-foreground">{o.count} · {fmtPct(pct)}</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full" style={{ width: `${pct * 100}%`, background: o.outcome === 'recovered' ? COLOR_RECOVERED : COLOR_CALLS }} />
                        </div>
                      </li>
                    );
                  })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Sem resultados ainda.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Chamadas recentes</CardTitle>
            <CardDescription>Atualiza em tempo real</CardDescription>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/chamadas">Ver todas <ArrowRight /></Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recent.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : recent.data?.items.length ? (
            <ul className="divide-y">
              {recent.data.items.map((c) => (
                <li key={c.id}>
                  <Link to={`/chamadas/${c.id}`} className="flex items-center gap-4 py-2.5 hover:bg-muted/50 -mx-2 px-2 rounded">
                    <StatusBadge label={CALL_STATUS_LABEL[c.status]} className={CALL_STATUS_VARIANT[c.status]} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{c.lead?.name || 'Sem nome'} <span className="font-normal text-muted-foreground">· {fmtPhone(c.toNumber)}</span></p>
                      <p className="truncate text-xs text-muted-foreground">{c.outcome ? OUTCOME_LABEL[c.outcome] : c.lead?.productCodename ?? '—'}</p>
                    </div>
                    <span className="text-xs tabular-nums text-muted-foreground">{fmtDuration(c.durationSec)}</span>
                    <span className="hidden text-xs text-muted-foreground sm:inline">{fmtRelative(c.createdAt)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhuma chamada ainda. Vá em <Link className="underline" to="/leads">Leads</Link> e clique em "Ligar agora".</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, loading, pulse }: { icon: typeof Users; label: string; value?: number | string; hint?: string; loading: boolean; pulse?: boolean }) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-5">
        <div className={`flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ${pulse ? 'animate-pulse' : ''}`}>
          <Icon className="size-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          {loading ? <Skeleton className="mt-1 h-7 w-16" /> : <p className="text-2xl font-semibold tabular-nums">{value ?? '—'}</p>}
          {hint && <p className="truncate text-xs text-muted-foreground">{hint}</p>}
        </div>
      </CardContent>
    </Card>
  );
}
